import type { WalkthroughStep } from './types';

// The demo walkthrough spine (R12) — a guided tour through the Pinnacle demo
// script (`docs/plans/stage2-show/plan.md`, "Demo script"). Gated behind
// `?demo=true` (see chrome/Walkthrough.tsx). One step per demo-script beat:
// viewer, UG scheduler, OP scheduler, assistant, export. The seeded demo
// project is `kgo` (Kalgarra Gold Operations) — see
// `src/versions/v1/shared/project-seed.ts`.
//
// Each step's `hash` carries `?demo=true` itself: Walkthrough.tsx navigates
// with a bare `navigate(target)`, which replaces the whole URL including the
// query string, so the gate would otherwise drop after the first advance.
//
// `anchorSelector` reuses each scene's existing `data-comment-anchor` root
// (or `data-probe="viewer-page"` for the viewer, which has no single control
// that covers "toggle a layer, then switch colour mode") — see the class
// comment on `WalkthroughStep` in `./types.ts`.
export const STEPS: WalkthroughStep[] = [
  {
    hash: '/projects/kgo/viewer?demo=true',
    persona: 'Viewer',
    caption:
      "Snap the model to North–South, switch off the Development layer to isolate the stopes, then change colour mode and read the legend's plain-English explainer.",
    breadcrumb: [
      { label: 'Kalgarra Gold Operations', hash: '/projects/kgo' },
      { label: 'Viewer', hash: '/projects/kgo/viewer?demo=true' },
    ],
    anchorSelector: '[data-probe="viewer-page"]',
  },
  {
    hash: '/projects/kgo/system/scheduler?demo=true',
    persona: 'Editor',
    caption:
      "Step through the underground scheduler's five input steps, adjust a development driver, run the schedule, then read the resulting rows and their check charts.",
    breadcrumb: [
      { label: 'Kalgarra Gold Operations', hash: '/projects/kgo' },
      { label: 'System', hash: '/projects/kgo/system?demo=true' },
      { label: 'Underground scheduler', hash: '/projects/kgo/system/scheduler?demo=true' },
    ],
    anchorSelector: '[data-comment-anchor="UgSchedulerScene"]',
  },
  {
    hash: '/projects/kgo/system/op-scheduler?demo=true',
    persona: 'Editor',
    caption:
      'Do the same for the open pit: set the excavator bench allocation, run the schedule, and slice the check charts by material class and mine area.',
    breadcrumb: [
      { label: 'Kalgarra Gold Operations', hash: '/projects/kgo' },
      { label: 'System', hash: '/projects/kgo/system?demo=true' },
      { label: 'Open-pit scheduler', hash: '/projects/kgo/system/op-scheduler?demo=true' },
    ],
    anchorSelector: '[data-comment-anchor="OpSchedulerScene"]',
  },
  {
    hash: '/projects/kgo/chat?demo=true',
    persona: 'Editor',
    caption:
      "A starter prompt, three follow-up chips from the assistant, then a check chart that links straight back to the scheduler it came from.",
    breadcrumb: [
      { label: 'Kalgarra Gold Operations', hash: '/projects/kgo' },
      { label: 'Assistant', hash: '/projects/kgo/chat?demo=true' },
    ],
    // Not `data-comment-anchor="ProjectChatScene"` — that name is only ever
    // stamped in its combined form ("ProjectChatScene.ChatShell", via the
    // nested Commentable inside ChatShell), and `scripts/check-comment-
    // anchors.mjs` rejects a dotted literal since it never matches a
    // top-level V1_ANCHORS key. `data-probe="composer"` (ChatComposer.tsx) is
    // the stable, un-composed hook for this beat's starting point instead.
    anchorSelector: '[data-probe="composer"]',
  },
  {
    hash: '/projects/kgo/export?demo=true',
    persona: 'Admin',
    caption:
      "Export the selected case into a single zip — its register row, period series, DataBook parameters and schedule rows bundled for delivery.",
    breadcrumb: [
      { label: 'Kalgarra Gold Operations', hash: '/projects/kgo' },
      { label: 'Export', hash: '/projects/kgo/export?demo=true' },
    ],
    anchorSelector: '[data-comment-anchor="ExportScene"]',
  },
];

// Resolve a path+search to a step index. Returns -1 if no match.
export function findStepByHash(hash: string): number {
  return STEPS.findIndex((s) => s.hash === hash);
}
